import React, { useEffect } from 'react';
import { translate } from '../../../utils/translation/translation';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { translationsState } from '../../../atoms/translation/translation.atom';
import styles from './ConfirmationDialog.module.scss';
import { useMutation } from '@tanstack/react-query';
import axios, { ErrorResponse } from '../../../libraries/axios/axios';
import { toast } from 'sonner';
import { colors } from '../../../styles/colors';
import { HttpMethod } from '../../../domain/Role';
import { modalOpenState } from '../../../atoms/layout/layout.atom';
import { SetValues } from '../../../hooks/useForm/useForm';

export type DeleteActionProps = {
  title: string;
  description: string;
  successMessage: string;
  apiUrl: string;
  onSuccess?: (data?: SetValues) => void;
  isOpen: boolean;
  onClose: () => void;
  bulkDto?: any;
  postDto?: any;
  httpMethod?: HttpMethod;
  onClick?: () => void;
};

export const ConfirmationDialog = ({
  title,
  description,
  successMessage,
  apiUrl,
  onSuccess,
  isOpen,
  onClose,
  bulkDto,
  postDto,
  httpMethod = HttpMethod.DELETE,
  onClick,
}: DeleteActionProps) => {
  const translations = useRecoilValue(translationsState);
  const setIsModalOpen = useSetRecoilState(modalOpenState);

  const { mutate, isPending, error } = useMutation<unknown, ErrorResponse>({
    mutationFn: () => {
      if (bulkDto) {
        return axios.post(apiUrl, bulkDto);
      }

      if (httpMethod === HttpMethod.DELETE) {
        return axios.delete(apiUrl);
      }

      if (httpMethod === HttpMethod.POST) {
        return axios.post(apiUrl, postDto);
      }

      return axios.patch(apiUrl);
    },
    onSuccess: (response: any) => {
      toast.success(successMessage);
      onSuccess?.(response?.data);
    },
  });

  useEffect(() => {
    setIsModalOpen(isOpen);
  }, [isOpen]);

  const handleClose = (e: any) => {
    e?.stopPropagation();

    setIsModalOpen(false);
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="responsive-dialog-title"
      onClick={(e) => e.stopPropagation()}
    >
      <DialogTitle id="responsive-dialog-title" className={styles.dialogTitle}>
        {title}
      </DialogTitle>
      <DialogContent
        className={styles.dialogContent}
        style={{ color: colors.textSubtitleColor }}
      >
        {description}
      </DialogContent>
      <DialogActions className={styles.dialogActions}>
        {error && (
          <Alert variant={'standard'} severity={'error'} color={'error'}>
            {translate(
              translations,
              error?.response?.data?.message?.toString() ?? '',
            )}
          </Alert>
        )}
        <Box className={styles.buttons}>
          <Button
            onClick={handleClose}
            variant={'outlined'}
            disabled={isPending}
          >
            {translate(translations, 'CONFIRMATION_DIALOG.DELETE_CANCEL')}
          </Button>
          <Button
            onClick={() => {
              if (onClick) {
                onClick();

                return;
              }

              mutate();
            }}
            variant={'contained'}
            disabled={isPending}
            startIcon={
              isPending ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {translate(translations, 'CONFIRMATION_DIALOG.DELETE_CONFIRM')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
